import { supabase } from './supabase';
import { toast } from 'sonner';

/**
 * Centralized logging utility for frontend errors.
 * Logs to:
 * 1. Console (Dev only)
 * 2. User Toast (Optional)
 * 3. Database (public.app_errors)
 */
export const logError = async (error, context = {}) => {
    const { 
        componentName = 'System', 
        errorType = 'frontend', 
        severity = 'error', 
        showToast = true,
        metadata = {}
    } = context;

    const errorMessage = error?.message || String(error);
    const errorStack = error?.stack || null;
    const url = window.location.href;

    // 1. Console Log for developers (Dev only)
    if (process.env.NODE_ENV !== 'production') {
        console.group(`🔴 Error in ${componentName}`);
        console.error('Message:', errorMessage);
        if (errorStack) console.error('Stack:', errorStack);
        console.groupEnd();
    }

    // 2. Display Toast to user if requested
    if (showToast) {
        toast.error(errorMessage, {
            description: 'This error has been logged for review.',
            duration: 5000,
        });
    }

    // 3. Persist to Database
    try {
        // Try to get user and profile from localStorage (faster than async call)
        let profile = {};
        try {
            profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        } catch {}

        const team_id = profile.team_id || metadata.team_id || null;
        const user_id = profile.id || null;

        await supabase.from('app_errors').insert([{
            team_id,
            user_id,
            error_message: errorMessage,
            error_stack: errorStack,
            error_type: errorType,
            url,
            component_name: componentName,
            severity,
            metadata: {
                ...metadata,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString()
            }
        }]);
    } catch (logErr) {
        // Silent failure for logging itself to avoid infinite loops
        console.warn('Logging utility failed to persist error:', logErr);
    }
};

/**
 * Higher-order function to wrap async calls with logging
 */
export const withLogging = (fn, context = {}) => async (...args) => {
    try {
        return await fn(...args);
    } catch (error) {
        await logError(error, context);
        throw error; // Re-throw to allow local handling if needed
    }
};
